import React from 'react';
import { Search, MapPin, Mail, Phone, User, Lock, Eye, EyeOff } from 'lucide-react';

const Input = ({
  type = 'text',
  placeholder = '',
  value = '',
  onChange = () => {},
  onBlur = () => {},
  onFocus = () => {},
  label = '',
  error = '',
  disabled = false,
  required = false,
  className = '',
  icon = null,
  iconPosition = 'left',
  size = 'md',
  variant = 'default',
  hideSpinner = true,
  preventTextInput = false,
  formatNumber = false,
  decimalPlaces = 2,
  ...props
}) => {
  const baseClasses = 'block w-full border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200';
  
  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base'
  };

  const variantClasses = {
    default: 'bg-white',
    filled: 'bg-gray-50',
    error: 'border-red-300 focus:ring-red-500 focus:border-red-500'
  };

  const iconClasses = {
    left: icon ? 'pl-10' : 'pl-3',
    right: icon ? 'pr-10' : 'pr-3'
  };

  const inputClasses = `
    ${baseClasses}
    ${sizeClasses[size]}
    ${variantClasses[error ? 'error' : variant]}
    ${iconClasses[iconPosition]}
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
    ${hideSpinner ? '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none' : ''}
    ${className}
  `.trim();

  const handleBlur = (e) => {
    let newValue = e.target.value;
    
    if (formatNumber && type === 'number' && newValue) {
      const numValue = parseFloat(newValue);
      if (!isNaN(numValue)) {
        newValue = numValue.toFixed(decimalPlaces);
        
        // Create a new event object with the formatted value
        const formattedEvent = {
          ...e,
          target: {
            ...e.target,
            value: newValue
          }
        };
        
        // Call onChange with the formatted value
        onChange(formattedEvent);
      }
    }
    
    onBlur(e);
  };

  const handleKeyDown = (e) => {
    if (preventTextInput && type === 'number') {
      // Allow: backspace, delete, tab, escape, enter, home, end, left, right, up, down
      if ([8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
          // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
          (e.keyCode === 65 && e.ctrlKey === true) ||
          (e.keyCode === 67 && e.ctrlKey === true) ||
          (e.keyCode === 86 && e.ctrlKey === true) ||
          (e.keyCode === 88 && e.ctrlKey === true)) {
        return;
      }
      
      // Allow decimal point (period) - keyCode 190 and 110
      if (e.keyCode === 190 || e.keyCode === 110) {
        // Check if decimal point already exists in the value
        if (e.target.value.indexOf('.') !== -1) {
          e.preventDefault();
        }
        return;
      }
      
      // Ensure that it is a number and stop the keypress
      if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
        e.preventDefault();
      }
    }
  };

  const handleChange = (e) => {
    let newValue = e.target.value;
    
    if (formatNumber && type === 'number') {
      // Remove any non-numeric characters except decimal point
      newValue = newValue.replace(/[^0-9.]/g, '');
      
      // Ensure only one decimal point
      const parts = newValue.split('.');
      if (parts.length > 2) {
        newValue = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // Limit decimal places
      if (parts.length === 2 && parts[1].length > decimalPlaces) {
        newValue = parts[0] + '.' + parts[1].substring(0, decimalPlaces);
      }
    }
    
    // Create a new event object with the formatted value
    const formattedEvent = {
      ...e,
      target: {
        ...e.target,
        value: newValue
      }
    };
    
    onChange(formattedEvent);
  };

  const renderIcon = () => {
    if (!icon) return null;

    const iconMap = {
      search: Search,
      location: MapPin,
      email: Mail,
      phone: Phone,
      user: User,
      lock: Lock,
      eye: Eye,
      'eye-off': EyeOff
    };

    const IconComponent = typeof icon === 'string' ? iconMap[icon] : icon;
    if (!IconComponent) return null;

    const iconSize = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5';
    const iconPositionClass = iconPosition === 'left' ? 'left-3' : 'right-3';

    return (
      <IconComponent className={`absolute ${iconPositionClass} top-1/2 transform -translate-y-1/2 text-gray-400 ${iconSize}`} />
    );
  };

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {renderIcon()}
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={formatNumber && type === 'number' ? handleChange : onChange}
          onBlur={formatNumber && type === 'number' ? handleBlur : onBlur}
          onFocus={onFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          required={required}
          className={inputClasses}
          {...props}
        />
      </div>
      
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default Input;
